import { useEffect } from 'react'

import { useThemeStore } from '@/store/theme.store'

const RADIUS_OPTIONS: Record<string, string> = {
  none: '0px',
  small: '4px',
  medium: '12px',
  large: '24px',
  full: '9999px',
}

/**
 * Applies the current theme store values as CSS custom properties on
 * `document.documentElement`.  Must be called once near the root of the
 * React tree (e.g. in AppLayout) so that every page inherits the tokens
 * regardless of whether the ThemePanel UI is rendered.
 */
export function useThemeApply() {
  const { accentHue, baseChroma, fontFamily, radius, formRadius, dark, theme, themeMode, setThemeMode } =
    useThemeStore()

  useEffect(() => {
    if (themeMode !== 'system') return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      // Re-trigger system mode evaluation to update `dark` state
      setThemeMode('system')
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [themeMode, setThemeMode])

  useEffect(() => {
    const root = document.documentElement

    // ── Dark / Light class ──────────────────────────────────────────
    if (dark) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Set the data-theme attribute for CSS theme files
    root.setAttribute('data-theme', theme)

    // Only apply manual style overrides if we are in 'Custom' mode.
    // Presets (Default, Sky, Airbnb, etc.) are driven by their CSS files.
    const isCustom = theme === 'Custom'

    if (!isCustom) {
      // Clear manual overrides for base variables to let CSS themes take over
      const baseProperties = [
        '--accent', '--accent-soft', '--accent-soft-foreground',
        '--background', '--foreground', '--surface', '--surface-secondary', '--surface-tertiary',
        '--overlay', '--default', '--muted', '--field-placeholder', '--field-background',
        '--border', '--separator', '--radius', '--form-radius', '--font-sans', '--font-heading'
      ]
      baseProperties.forEach(p => root.style.removeProperty(p))
    } else {
      // ── Accent ──────────────────────────────────────────────────────
      root.style.setProperty('--accent', `oklch(0.6204 0.195 ${accentHue})`)
      root.style.setProperty(
        '--accent-soft',
        dark
          ? `oklch(0.25 0.04 ${accentHue})`
          : `oklch(0.95 0.02 ${accentHue})`,
      )
      root.style.setProperty(
        '--accent-soft-foreground',
        dark
          ? `oklch(0.85 0.08 ${accentHue})`
          : `oklch(0.4 0.15 ${accentHue})`,
      )
    }

    // ── Primary colour ramp ─────────────────────────────────────────
    // If it's a preset, we might still want a primary ramp calculated from accentHue 
    // IF the theme doesn't define its own ramp. 
    // For now, let's always calculate it so utilities like bg-primary-100 work.
    for (let i = 50; i <= 950; i += i < 100 ? 50 : 100) {
      const lightness = dark
        ? (i / 1000) * 0.8 + 0.1
        : 1 - i / 1000
      const chroma =
        i < 500 ? 0.01 + i / 2500 : 0.19 - (i - 500) / 5000
      root.style.setProperty(
        `--color-primary-${i}`,
        `oklch(${lightness.toFixed(2)} ${chroma.toFixed(2)} ${accentHue})`,
      )
    }

    if (isCustom) {
      // ── Base / Background / Foreground + semantic tokens ────────────
      const c = baseChroma
      const h = accentHue
      const half = c / 2

      if (dark) {
        root.style.setProperty('--background',          `oklch(0.145 ${half} ${h})`)
        root.style.setProperty('--foreground',           `oklch(0.98 0 0)`)

        root.style.setProperty('--surface',              `oklch(0.21 ${half} ${h})`)
        root.style.setProperty('--surface-secondary',    `oklch(0.257 ${half * 0.6} ${h})`)
        root.style.setProperty('--surface-tertiary',     `oklch(0.272 ${half * 0.4} ${h})`)

        root.style.setProperty('--overlay',              `oklch(0.21 ${half} ${h})`)
        root.style.setProperty('--default',              `oklch(0.274 ${half * 0.8} ${h})`)

        root.style.setProperty('--muted',                `oklch(0.705 ${Math.min(c * 0.3, 0.015)} ${h})`)
        root.style.setProperty('--field-placeholder',    `oklch(0.705 ${Math.min(c * 0.3, 0.015)} ${h})`)

        root.style.setProperty('--field-background',     `oklch(0.21 ${half} ${h})`)

        root.style.setProperty('--border',               `oklch(0.28 ${half * 0.8} ${h})`)
        root.style.setProperty('--separator',            `oklch(0.25 ${half * 0.8} ${h})`)
      } else {
        root.style.setProperty('--background',           `oklch(0.98 ${c} ${h})`)
        root.style.setProperty('--foreground',           `oklch(0.2103 0.0059 285.89)`)

        root.style.setProperty('--surface',              `oklch(1 ${c * 0.5} ${h})`)
        root.style.setProperty('--surface-secondary',    `oklch(0.952 ${c * 0.7} ${h})`)
        root.style.setProperty('--surface-tertiary',     `oklch(0.937 ${c * 0.7} ${h})`)

        root.style.setProperty('--overlay',              `oklch(1 ${c * 0.5} ${h})`)
        root.style.setProperty('--default',              `oklch(0.94 ${c * 0.6} ${h})`)

        root.style.setProperty('--muted',                `oklch(0.552 ${Math.min(c * 0.5, 0.015)} ${h})`)
        root.style.setProperty('--field-placeholder',    `oklch(0.552 ${Math.min(c * 0.5, 0.015)} ${h})`)

        root.style.setProperty('--field-background',     `oklch(1 ${c * 0.5} ${h})`)

        root.style.setProperty('--border',               `oklch(0.90 ${c * 0.6} ${h})`)
        root.style.setProperty('--separator',            `oklch(0.92 ${c * 0.6} ${h})`)
      }

      // ── Fonts ───────────────────────────────────────────────────────
      root.style.setProperty('--font-sans', `"${fontFamily}", ui-sans-serif, system-ui, sans-serif`)
      root.style.setProperty('--font-heading', `"${fontFamily}", ui-sans-serif, system-ui, sans-serif`)

      // ── Radius ──────────────────────────────────────────────────────
      const radiusValue = RADIUS_OPTIONS[radius] ?? radius
      const formRadiusValue = RADIUS_OPTIONS[formRadius] ?? formRadius
      root.style.setProperty('--radius', radiusValue)
      root.style.setProperty('--form-radius', formRadiusValue)
    }
  }, [accentHue, baseChroma, fontFamily, radius, formRadius, dark, theme])
}
